import React, {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {NotificationManager} from 'react-notifications';
import {API_BASE, API_ENDPOINT} from '../config';
import {useAuth} from '../auth/AuthContext';

/**
 * Logo-ul companiei = iconita aplicatiei generate. Fabrica il descarca inainte de
 * build (/api/internal/companies/{code}/logo); fara el generarea APK-ului se
 * opreste, de aceea avertizam vizibil cand lipseste.
 */
const LOGO_MIN_SIZE = 512;

const CompanyLogo = () => {
    const {auth, logout} = useAuth();
    const [companies, setCompanies] = useState([]);
    const [activeCode, setActiveCode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const authHeader = {Authorization: `Bearer ${auth.token}`};

    const loadMe = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await axios.get(`${API_ENDPOINT}/v1/me`, {headers: authHeader});
            const list = data.companies || [];
            setCompanies(list);
            setActiveCode((prev) => prev && list.some(c => c.code === prev) ? prev : (list[0]?.code ?? null));
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
            } else {
                NotificationManager.error('Nu am putut încărca datele companiei.');
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token]);

    useEffect(() => {
        loadMe();
    }, [loadMe]);

    const activeCompany = companies.find(c => c.code === activeCode) || null;
    const logo = activeCompany?.logo || null;

    const handleFile = async (file) => {
        if (!activeCompany || !file) {
            return;
        }
        setUploading(true);
        const form = new FormData();
        form.append('logoFile', file);
        try {
            await axios.post(
                `${API_ENDPOINT}/v1/me/companies/${activeCompany.code}/logo`,
                form,
                {headers: {...authHeader, 'Content-Type': 'multipart/form-data'}}
            );
            NotificationManager.success('Logo încărcat.');
            await loadMe();
        } catch (err) {
            // 422 vine cu motivul exact (prea mic / ne-patrat) - il aratam ca atare
            NotificationManager.error(err.response?.data?.error || 'Eroare la încărcarea logo-ului.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDelete = async () => {
        if (!activeCompany || !window.confirm('Ștergi logo-ul? Fără el, generarea APK-ului se va opri.')) {
            return;
        }
        try {
            await axios.delete(
                `${API_ENDPOINT}/v1/me/companies/${activeCompany.code}/logo`,
                {headers: authHeader}
            );
            NotificationManager.success('Logo șters.');
            await loadMe();
        } catch (err) {
            NotificationManager.error('Nu am putut șterge logo-ul.');
        }
    };

    if (loading) {
        return <div className="card"><div className="card-body">Se încarcă…</div></div>;
    }

    if (!activeCompany) {
        return <div className="card"><div className="card-body">Nicio companie asociată contului.</div></div>;
    }

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">Logo aplicație</h3>
            </div>
            <div className="card-body">
                {companies.length > 1 && (
                    <div className="form-group">
                        <label htmlFor="logo-company">Companie</label>
                        <select
                            id="logo-company"
                            className="form-control"
                            value={activeCode || ''}
                            onChange={(e) => setActiveCode(e.target.value)}
                        >
                            {companies.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                    </div>
                )}

                {logo ? (
                    <div className="mb-3">
                        <img
                            src={`${API_BASE}${logo.thumbUrl || logo.contentUrl}`}
                            alt="Logo companie"
                            style={{width: 128, height: 128, objectFit: 'contain', background: '#f4f6f9', borderRadius: 8}}
                        />
                        <div className="mt-2 text-muted">
                            {logo.name} — {logo.width}×{logo.height} px
                        </div>
                        <button type="button" className="btn btn-outline-danger btn-sm mt-2" onClick={handleDelete}>
                            Șterge logo
                        </button>
                    </div>
                ) : (
                    <div className="alert alert-warning">
                        Compania nu are logo. <strong>Generarea APK-ului nu va porni</strong> până nu încarci unul.
                    </div>
                )}

                <div className="form-group">
                    <label htmlFor="logo-file">{logo ? 'Înlocuiește logo-ul' : 'Încarcă logo'}</label>
                    <input
                        id="logo-file"
                        type="file"
                        ref={fileInputRef}
                        className="form-control-file"
                        accept="image/png,image/jpeg"
                        disabled={uploading}
                        onChange={(e) => handleFile(e.target.files?.[0])}
                    />
                    <small className="form-text text-muted">
                        PNG sau JPG, pătrat, minim {LOGO_MIN_SIZE}×{LOGO_MIN_SIZE} px. Fundalul transparent e permis.
                        Din el se generează automat toate mărimile de iconiță și ecranul de pornire.
                    </small>
                </div>

                {uploading && <div className="text-muted">Se încarcă…</div>}
            </div>
        </div>
    );
};

export default CompanyLogo;
